import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useLocalStorageState from 'use-local-storage-state';

interface DataWithVersion {
  _version?: number;
}

interface LocalStorageProperties {
  isPersistent: boolean;
  removeItem: () => void;
}

const applyVersion = <T>(data: T, version: number) => ({
  ...data,
  _version: version,
});

const migrateIfNeeded = <T extends DataWithVersion>(data: T, migrations: ((data: T) => T)[]): T => {
  let transformedData = data;
  const transformedDataVersion = data._version ?? 0;
  const currentVersion = migrations.length;

  // Only migrate if version is outdated
  if (transformedDataVersion >= currentVersion) {
    return transformedData as T;
  }

  // Migrate from current version to target version
  for (let i = transformedDataVersion; i < currentVersion; i++) {
    const nextStep = migrations[i];
    if (nextStep === undefined) break;

    const transformationResult = nextStep(transformedData);
    transformedData = applyVersion(transformationResult, i + 1);
  }

  return transformedData as T;
};

export const useLocalStorageStateWithMigrations = <T extends DataWithVersion>(
  key: string,
  defaultValue: T,
  migrations: ((data: T) => T)[]
): [T, (updater: (previousState: T) => T) => void, LocalStorageProperties] => {
  const [state, updateState, ...rest] = useLocalStorageState(key, { defaultValue });

  const currentVersion = useMemo(() => migrations.length, [migrations]);

  // Apply migrations only once when state loads
  const [migratedState, setMigratedState] = useState<T>(state);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      const migrated = migrateIfNeeded<T>(state, migrations);

      if (migrated._version !== state._version) {
        // Save migrated state to localStorage
        updateState(() => migrated);
        setMigratedState(migrated);
      } else {
        setMigratedState(state);
      }
    } else {
      // After initialization, just use the current state
      setMigratedState(state);
    }
  }, [state, migrations, updateState]);

  const updateStateWithVersion = useCallback(
    (updater: (previousState: T) => T) => {
      updateState(previousState => {
        const state = updater(previousState);

        return applyVersion(state, currentVersion);
      });
    },
    [currentVersion, updateState]
  );

  return [migratedState, updateStateWithVersion, ...rest];
};
